import React, { useEffect, useMemo, useState } from 'react';
import { 
  Box, 
  CssBaseline, 
  ThemeProvider, 
  createTheme, 
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Dashboard from './pages/Dashboard';
import Header from './components/Header';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './pages/Login';
import SSOLogin from './pages/SSOLogin';
import ForgotPassword from './pages/ForgotPassword';
import Register from './pages/Register';
import Profile from './pages/Profile';
import IndividualNumbers from './pages/IndividualNumbers';
import Analytics from './pages/Analytics';
import DataManagement from './pages/DataManagement';
import AgentLifecycle from './pages/AgentLifecycle';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import StartDatePrompt from './components/StartDatePrompt';

import NewAgentOnboarding from './pages/NewAgentOnboarding';
import TrainingResources from './pages/TrainingResources';
import HierarchyAdjustments from './pages/HierarchyAdjustments';
import Compensation from './pages/Compensation';
import ScriptsForms from './pages/ScriptsForms';
import Testimonies from './pages/Testimonies';

const PrivateRoute = ({ children }) => {
  const [accessState, setAccessState] = useState('checking');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch('/api/auth/me', { credentials: 'include', headers })
      .then((response) => setAccessState(response.ok ? 'granted' : 'denied'))
      .catch(() => setAccessState('denied'));
  }, []);

  if (accessState === 'checking') {
    return (
      <main style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '100vh' }}>
        <span style={{ color: '#64748b', fontFamily: 'system-ui, sans-serif' }}>Opening leaderboard…</span>
      </main>
    );
  }

  return accessState === 'granted' ? children : <MovedLeaderboardPage />;
};

const PAYD_HUB_URL = 'https://paydhub.com';

const MovedLeaderboardPage = () => {
  return (
    <main style={{
      alignItems: 'center',
      background: 'linear-gradient(135deg, #f0fdf7 0%, #f8fafc 50%, #eff6ff 100%)',
      boxSizing: 'border-box',
      display: 'flex',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 24,
    }}>
      <section style={{
        background: '#fff',
        border: '1px solid #d1fae5',
        borderRadius: 20,
        boxShadow: '0 16px 40px rgba(15, 23, 42, 0.10)',
        maxWidth: 440,
        padding: '48px 36px',
        textAlign: 'center',
        width: '100%',
      }}>
        <div style={{
          alignItems: 'center',
          background: '#d1fae5',
          borderRadius: 16,
          color: '#047857',
          display: 'inline-flex',
          fontSize: 28,
          height: 56,
          justifyContent: 'center',
          marginBottom: 22,
          width: 56,
        }}>↗</div>
        <h1 style={{ color: '#0f172a', fontSize: 26, margin: '0 0 12px' }}>This link has been moved.</h1>
        <p style={{ color: '#64748b', fontSize: 16, lineHeight: 1.6, margin: '0 0 28px' }}>
          Log in through Payd Hub to access the Leaderboard.
        </p>
        <a
          href={PAYD_HUB_URL}
          style={{
            background: '#059669',
            borderRadius: 10,
            color: '#fff',
            display: 'block',
            fontSize: 16,
            fontWeight: 700,
            padding: '14px 20px',
            textDecoration: 'none',
          }}
        >
          Go to Payd Hub
        </a>
      </section>
    </main>
  );
};

// Main App component that uses Router
function App() {
  const muiTheme = useMemo(
    () => createTheme({
      palette: {
        mode: 'light',
        primary: {
          main: '#2C9C6C',
          light: '#4DC896',
          dark: '#1F6F52',
        },
        secondary: {
          main: '#c9a84c',
          light: '#d4bc73',
          dark: '#a88a35',
        },
        background: {
          default: '#F5F7F6',
          paper: '#FFFFFF',
        },
        text: {
          primary: '#1A2B24',
          secondary: '#5A6B63',
          disabled: '#8A9B93',
        },
        divider: '#E0E5E3',
        error: { main: '#DC2626' },
        action: {
          hover: 'rgba(44, 156, 108, 0.06)',
          selected: 'rgba(44, 156, 108, 0.12)',
        },
      },
      typography: {
        fontFamily: [
          'Inter',
          'Archivo',
          '-apple-system',
          'BlinkMacSystemFont',
          '"Segoe UI"',
          'Roboto',
          '"Helvetica Neue"',
          'Arial',
          'sans-serif',
        ].join(','),
        h1: { fontWeight: 700, letterSpacing: '-0.01em' },
        h2: { fontWeight: 700, letterSpacing: '-0.01em' },
        h3: { fontWeight: 600, letterSpacing: '-0.01em' },
        h4: { fontWeight: 600, letterSpacing: '-0.01em' },
        h5: { fontWeight: 500, letterSpacing: '-0.01em' },
        h6: { fontWeight: 500, letterSpacing: '-0.01em' },
        button: { fontWeight: 500, textTransform: 'none', letterSpacing: '0.01em' },
      },
      shape: {
        borderRadius: 12,
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: '#F5F7F6',
              color: '#1A2B24',
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(12px)',
              borderBottom: '1px solid #E0E5E3',
              boxShadow: 'none',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 8,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            },
            contained: {
              background: 'linear-gradient(135deg, #1F6F52 0%, #2C9C6C 100%)',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #2C9C6C 0%, #4DC896 100%)',
                boxShadow: 'none',
              },
            },
            outlined: {
              borderColor: '#E0E5E3',
              '&:hover': { borderColor: '#2C9C6C', backgroundColor: 'rgba(44, 156, 108, 0.06)' },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E0E5E3',
              borderRadius: 16,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              borderColor: '#E0E5E3',
              color: '#5A6B63',
              '&.Mui-selected': {
                backgroundColor: 'rgba(44, 156, 108, 0.12)',
                color: '#2C9C6C',
                '&:hover': { backgroundColor: 'rgba(44, 156, 108, 0.2)' },
              },
            },
          },
        },
        MuiBadge: {
          styleOverrides: {
            badge: { backgroundColor: '#c9a84c' },
          },
        },
        MuiSelect: {
          styleOverrides: {
            select: { '&.Mui-selected': { backgroundColor: 'transparent' } },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              '&.Mui-selected': {
                backgroundColor: 'rgba(44, 156, 108, 0.16)',
                '&:hover': { backgroundColor: 'rgba(44, 156, 108, 0.24)' },
              },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: { borderColor: '#E0E5E3' },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: { borderColor: '#E0E5E3' },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              backgroundColor: '#FFFFFF',
              border: '1px solid #E0E5E3',
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#E0E5E3' },
                '&:hover fieldset': { borderColor: '#2C9C6C' },
              },
            },
          },
        },
      },
    }),
    []
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

// Inner component that can use Router hooks like useNavigate
function AppContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();

  // Common page layout
  const PageLayout = ({ children }) => (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      background: 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(44, 156, 108, 0.06) 0%, transparent 70%)',
      backgroundAttachment: 'fixed',
    }}>
      <Header
        navigate={navigate}
        isMobile={isMobile}
      />
      <StartDatePrompt />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          backgroundColor: 'background.default',
          minHeight: '100vh',
          marginTop: '80px',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        {children}
      </Box>
    </Box>
  );

  const PublicLeaderboardLayout = ({ children }) => (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        p: { xs: 2, sm: 3 },
        backgroundColor: 'background.default',
        background: 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(44, 156, 108, 0.06) 0%, transparent 70%)',
      }}
    >
      {children}
    </Box>
  );

  return (
    <Routes>
      <Route path="/login" element={<MovedLeaderboardPage />} />
      <Route path="/forgot-password" element={<MovedLeaderboardPage />} />
      <Route path="/register" element={<MovedLeaderboardPage />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <PageLayout>
              <Profile />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <PageLayout>
              <Analytics />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-management"
        element={
          <AdminRoute>
            <PageLayout>
              <DataManagement />
            </PageLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/agent-lifecycle"
        element={
          <AdminRoute>
            <PageLayout>
              <AgentLifecycle />
            </PageLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <PublicLeaderboardLayout>
              <Dashboard />
            </PublicLeaderboardLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/individual"
        element={
          <ProtectedRoute>
            <PageLayout>
              <IndividualNumbers />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <PageLayout>
              <NewAgentOnboarding />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training"
        element={
          <ProtectedRoute>
            <PageLayout>
              <TrainingResources />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hierarchy"
        element={
          <ProtectedRoute>
            <PageLayout>
              <HierarchyAdjustments />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/compensation"
        element={
          <ProtectedRoute>
            <PageLayout>
              <Compensation />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scripts-forms"
        element={
          <ProtectedRoute>
            <PageLayout>
              <ScriptsForms />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/testimonies"
        element={
          <ProtectedRoute>
            <PageLayout>
              <Testimonies />
            </PageLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/sso" element={<SSOLogin />} />
      <Route path="/" element={<MovedLeaderboardPage />} />
      <Route path="*" element={<MovedLeaderboardPage />} />
    </Routes>
  );
}

export default App;
